import { Box, Typography, Stack } from "@mui/material";
import Calendar from "react-calendar";
import moment from "moment";
import "react-calendar/dist/Calendar.css";
import { useLocation } from "react-router-dom";

const AppointmentCalendar = ({
  bookedDates = [],
  selectedDate,
  onDateChange,
  type,
  appointments = [],
  weeklySchedule = [],
}) => {
  const location = useLocation();

  const isSpecialistDetailsPage = location.pathname.includes(
    "my-specialist-details"
  );
  const isMyMembersPage = location.pathname.includes("my-members");

  const hasAvailableSlots = (date) => {
    if (!weeklySchedule || weeklySchedule.length === 0) return false;
    const dateStr = moment(date).format("YYYY-MM-DD");
    const dayName = moment(date).format("dddd").toUpperCase();
    const daySchedule = weeklySchedule.find((s) => s.day === dayName);
    if (!daySchedule || !daySchedule.slots || daySchedule.slots.length === 0)
      return false;
    const bookedDate = bookedDates.find((d) => d.date === dateStr);
    const bookedSlotsCount = bookedDate?.bookedSlots?.length || 0;
    return daySchedule.slots.length > bookedSlotsCount;
  };

  const getAvailableSlotCount = (date) => {
    if (!weeklySchedule || weeklySchedule.length === 0) return 0;
    const dateStr = moment(date).format("YYYY-MM-DD");
    const dayName = moment(date).format("dddd").toUpperCase();
    const daySchedule = weeklySchedule.find((s) => s.day === dayName);
    if (!daySchedule || !daySchedule.slots || daySchedule.slots.length === 0)
      return 0;
    const bookedDate = bookedDates.find((d) => d.date === dateStr);
    const bookedSlotsCount = bookedDate?.bookedSlots?.length || 0;
    return Math.max(0, daySchedule.slots.length - bookedSlotsCount);
  };

  const getAppointmentCount = (dateStr) => {
    if (type && type !== "all") {
      return appointments.filter(
        (a) =>
          moment(a.startTime).format("YYYY-MM-DD") === dateStr &&
          a.appointmentStatus === type
      ).length;
    }
    const bookedDate = bookedDates.find((d) => d.date === dateStr);
    return bookedDate?.count || 0;
  };

  const tileContent = ({ date, view }) => {
    if (view === "month") {
      const dateStr = moment(date).format("YYYY-MM-DD");
      const today = moment().startOf("day");
      const tileDate = moment(date).startOf("day");
      const isPastDate = tileDate.isBefore(today);

      if (isSpecialistDetailsPage) {
        if (!isPastDate) {
          const availableCount = getAvailableSlotCount(date);
          if (availableCount > 0) {
            return (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 2,
                  left: "20%",
                  transform: "translateX(-50%)",
                  backgroundColor: "#587bc5db",
                  color: "white",
                  borderRadius: "50%",
                  width: 14,
                  height: 14,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.55rem",
                  fontWeight: 700,
                }}
              >
                {availableCount}
              </Box>
            );
          }
        }
      } else {
        if (type === "pending") return null;

        const count = getAppointmentCount(dateStr);
        if (count > 0) {
          return (
            <Box
              sx={{
                position: "absolute",
                bottom: 2,
                left: "20%",
                transform: "translateX(-50%)",
                backgroundColor: "#73b1e3",
                color: "white",
                borderRadius: "50%",
                width: 14,
                height: 14,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.55rem",
                fontWeight: 700,
                opacity: isMyMembersPage && isPastDate ? 0.5 : 1,
              }}
            >
              {count}
            </Box>
          );
        }
      }
    }
    return null;
  };

  const tileClassName = ({ date, view }) => {
    if (view === "month") {
      const dateStr = moment(date).format("YYYY-MM-DD");
      const today = moment().startOf("day");
      const tileDate = moment(date).startOf("day");
      const isPastDate = tileDate.isBefore(today);

      if (isSpecialistDetailsPage) {
        if (!isPastDate && hasAvailableSlots(date)) {
          return "has-appointments";
        }
      } else {
        const hasBooking = bookedDates.some((d) => d.date === dateStr);
        if (hasBooking) {
          if (isMyMembersPage && isPastDate) {
            return "has-appointments past-date";
          }
          return "has-appointments";
        }
      }
    }
    return null;
  };

  const tileDisabled = ({ date, view }) => {
    if (view === "month" && (isSpecialistDetailsPage || isMyMembersPage)) {
      const today = moment().startOf("day");
      const tileDate = moment(date).startOf("day");
      return tileDate.isBefore(today);
    }
    return false;
  };

  return (
    <Box
      sx={{
        ".react-calendar": {
          border: "none",
          borderRadius: "8px",
          fontFamily: "inherit",
          width: "100%",
        },
        ".react-calendar__navigation__label": {
          color: "#302d2d",
          fontWeight: "600",
        },
        ".react-calendar__month-view__days": {
          display: "grid !important",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: "8px",
        },
        ".react-calendar__month-view__days__day--weekend": {
          color: "inherit !important",
        },
        ".react-calendar__tile": {
          position: "relative",
          margin: "0 !important",
          padding: "12px 6px",
          borderRadius: "8px",
          transition: "all 0.2s",
        },
        ".react-calendar__tile.has-appointments": {
          backgroundColor: "#bbe3fb93",
          fontWeight: "600",
          color: "#5ba4cd",
        },
        ".react-calendar__tile.has-appointments.past-date": {
          backgroundColor: "#f5f5f5",
          color: "#bdbdbd",
          opacity: "0.6",
        },
        ".react-calendar__tile--active, .react-calendar__month-view__days__day--weekend.react-calendar__tile--active": {
          backgroundColor: "#ef858566 !important",
          color: "#ffffffff !important",
          fontWeight: "600",
        },
        ".react-calendar__tile--now": {
          backgroundColor: "#fff9c4",
          fontWeight: "600",
        },
        ".react-calendar__tile:disabled": {
          backgroundColor: "#fafafa",
          color: "#bdbdbd",
          cursor: "not-allowed",
        }
      }}
    >
      < Calendar
        locale="en-US"
        minDetail="month"
        minDate={new Date()}
        value={selectedDate}
        onChange={onDateChange}
        tileContent={tileContent}
        tileClassName={tileClassName}
        tileDisabled={tileDisabled}
      />

      <Box sx={{ mt: 3 }}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: 0.5,
                bgcolor: "#bbe3fb93",
                border: "1px solid #c8e6c9",
              }}
            />
            <Typography variant="body2">
              {isSpecialistDetailsPage ? "Available Slots" : "Has Appointments"}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: 0.5,
                bgcolor: "#fff9c4",
                border: "1px solid #fff59d",
              }}
            />
            <Typography variant="body2">Today</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: 0.5,
                bgcolor: "#ef858566 ",
              }}
            />
            <Typography variant="body2">Selected</Typography>
          </Stack>
        </Stack>
      </Box>
    </Box >
  );
};

export default AppointmentCalendar;
